import { makeStyles } from '@material-ui/core';
import { Tag } from 'antd';
import qs from 'qs';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Loader from 'src/component/Loader/Loader';
import { useDownloadFile } from 'src/reactQuery/Content/useDownloadFile';
import { useGetContentsQuery } from 'src/reactQuery/Content/useGetContents';
import { useGetContentModelsQuery } from 'src/reactQuery/contentModels/queries/useGetContentModelsQuery';
import { isCurrentProjectGulfMap } from 'src/util/projectCheckers';
import ContentDrawer from './ContentDrawer';
import { fetchContent, setContentDraft } from '../../actions/ContentAction';
import CButton from '../../component/Button';
import Copy from '../../component/Copy';
import InnerHeader from '../../component/InnerHeader';
import { CStandardInput } from '../../component/Input';
import Modal from '../../component/Modal/Modal';
import NoxTable from '../../component/Table';
import formatDate from '../../helpers/formatDate';
import { createContent, deleteContent, duplicateContent } from '../../services/napi';

const useStyles = makeStyles({
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  }
});

const Content = () => {
  const history = useHistory();
  const urlLocation = useLocation();
  const dispatch = useDispatch();
  const { data: customTypes, isLoading: isLoadingContentModel } = useGetContentModelsQuery();
  const [drawerVisible, setDrawerVisible] = React.useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDragAndDropEnabled, setIsDragAndDropEnabled] = useState(false);
  const [isDuplicateSaveEnabled, setDuplicateSaveEnabled] = useState(true);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const fileDownloadMutation = useDownloadFile({});

  const projectId = useSelector(({ project }) => project.currentProject._id);
  const contentDraft = useSelector(({ content }) => content.contentDraft);

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });
  const isFilterArray = Array.isArray(params.filter);

  const { data: contentData, isLoading: isLoadingContent } = useGetContentsQuery({
    customTypeId: isFilterArray ? params.filter[0] : params.filter,
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    page: params.page,
    pageSize: isCurrentProjectGulfMap ? 10000000000 : params.pageSize,
    searchItem: params.search
  });

  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible);
  };

  const onContentTypeSelected = (customType) => {
    history.push({
      pathname: `/content/new`,
      state: { customType }
    });
  };

  const mapToTable = (content) => {
    const data = [];

    if (!content || content.length === 0) {
      return data;
    }

    content.forEach(
      // eslint-disable-next-line camelcase
      ({ _id, name, customTypeName, created, updated, status, custom_type }, index) => {
        const createdDate = formatDate(created);
        const updatedDate = formatDate(updated);
        // if (!customTypeName) return;
        data.push({
          _id,
          by: 'Me',
          contentType: customTypeName,
          created: createdDate,
          custom_type,
          key: index,
          name,
          status,
          updated: updatedDate
        });
      }
    );
    return data;
  };

  const onSaveDuplicate = (e) => {
    e.preventDefault();
    setDuplicateSaveEnabled(false);
    // eslint-disable-next-line no-restricted-syntax
    for (const index of selectedRowKeys) {
      const item = data[index];
      dispatch(
        contentDuplicate(
          {
            api_id: contentDraft.api_id,
            contentId: item._id,
            customTypeId: item.custom_type,
            name: contentDraft.name,
            numberOfCopies: contentDraft.numberOfCopies || 1
          },
          () => dispatch(fetchContent())
        )
      );
    }
    setSelectedRowKeys([]);
    setDuplicateSaveEnabled(true);
    setOpenDuplicateModal(false);
  };
  const onDuplicate = () => {
    setOpenDuplicateModal(true);
  };

  const onDuplicateCancel = () => {
    setOpenDuplicateModal(false);
  };

  const openDetail = (item) => {
    history.push(`/content/${item._id}`);
  };

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
              // width: "256px",
              // textAlign: "start",
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
      title: 'Name'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          key={status}
          style={
            status === 'published'
              ? {
                  backgroundColor: 'rgba(65, 204, 156, 0.15)',
                  border: 'none',
                  color: '#41CC9C',
                  width: '100%'
                }
              : {
                  backgroundColor: 'rgba(244, 211, 37, 0.15)',
                  border: 'none',
                  color: '#F4CD00',
                  width: '100%'
                }
          }
        >
          {status}
        </Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      dataIndex: 'contentType',
      key: 'contentType',
      render: (contentType) => {
        return <Copy text={contentType} />;
      },
      sorter: true,
      title: 'Content Model'
    },
    {
      dataIndex: 'created',
      key: 'created',
      render: (created) => {
        return <Copy text={created} />;
      },
      sorter: true,
      title: 'Created'
    },
    {
      dataIndex: 'updated',
      key: 'updated',
      render: (update) => {
        return <Copy text={update} />;
      },
      sorter: (a, b) => new Date(a.updated) - new Date(b.updated),
      title: 'Updated'
    },
    {
      dataIndex: 'by',
      key: 'by',
      render: (by) => {
        return <Copy text={by} />;
      },
      sorter: (a, b) => a.by.localeCompare(b.by),
      title: 'Author'
    },
    {
      key: 'edit',
      render: (item) => {
        return <Tag onClick={() => openDetail(item)}>edit</Tag>;
      }
    }
  ];

  let totalCopies;
  const classes = useStyles();

  const onContentSave = async (draft) => {
    const contentData = await createContent(draft, draft.project);
    return contentData;
  };

  const onContentDelete = async () => {
    await Promise.all(
      selectedRowKeys.map(async (index) => {
        const item = data[index];

        await deleteContent(item._id, projectId);
      })
    );

    setSelectedRowKeys([]);
    dispatch(fetchContent());
  };

  const getInitialFilterFromUrl = () => {
    const { filter } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    if (typeof filter === 'string') {
      const selectedCustomType = customTypes?.find((customType) => customType._id === filter);
      const newFilter = selectedCustomType?.name;
      return [newFilter, 'All'];
    }

    if (filter && filter[0] && filter[0] !== 'All') {
      const customTypeId = filter[0];
      const selectedCustomType = customTypes?.find((customType) => customType._id === customTypeId);
      filter[0] = selectedCustomType?.name;
    }

    return filter || [filterListContentModel[0], filterListStatus[0]];
  };

  const actions = isCurrentProjectGulfMap
    ? [
        {
          onClick: toggleDrawer,
          title: 'Add Content',
          type: 'primary'
        },
        {
          onClick: () => fileDownloadMutation.mutate(),
          title: 'Download Stations',
          type: 'primary'
        }
      ]
    : [
        {
          onClick: toggleDrawer,
          title: 'Add Content',
          type: 'primary'
        }
      ];

  if (selectedRowKeys.length) {
    actions.unshift(
      {
        onClick: onContentDelete,
        title: 'Delete'
      },
      {
        onClick: onDuplicate,
        title: 'Duplicate',
        type: 'secondaryModified'
      }
    );
  }

  if (isLoadingContent && isLoadingContentModel) {
    return <Loader />;
  }

  const data = mapToTable(contentData?.content);

  const filterListContentModel = ['All', ...new Set(customTypes?.map((item) => item.name))];
  const filterListStatus = ['All', ...new Set(data.map((item) => item.status))];

  const handleNameChange = (value) => {
    if (!/[^a-zA-Z0-9- ]/.test(value)) {
      dispatch(
        setContentDraft({
          ...contentDraft,
          name: value
        })
      );
    }
  };

  const handleCopiesChange = (value) => {
    dispatch(
      setContentDraft({
        ...contentDraft,
        api_id: contentDraft.api_id,
        name: contentDraft.name,
        numberOfCopies: value
      })
    );
  };

  // eslint-disable-next-line no-unused-vars
  const onFilterChange = (value, index, selectedFilter) => {
    if (index === 0) {
      if (value !== 'All') {
        const selectedCustomType = customTypes?.filter(
          (customType) => customType.name === value
        )[0];
        params.filter = [selectedCustomType._id, selectedFilter];
      } else {
        params.filter = [value, selectedFilter];
      }
    } else if (selectedFilter !== 'All') {
      const selectedCustomType = customTypes?.filter(
        (customType) => customType.name === selectedFilter
      )[0];
      params.filter = [selectedCustomType._id, value];
    } else {
      params.filter = [selectedFilter, value];
    }

    if (!params.filter[0] || params.filter[0] === 'All') {
      setIsDragAndDropEnabled(false);
    } else {
      setIsDragAndDropEnabled(true);
    }

    history.push({
      pathname: `/content`,
      search: qs.stringify(params)
    });
  };

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={contentData?.paging.total} title="Content" />
      <ContentDrawer
        items={contentData?.content}
        onClick={onContentTypeSelected}
        onClose={toggleDrawer}
        onContentSave={onContentSave}
        selectedFilter={getInitialFilterFromUrl()}
        setVisible={setDrawerVisible}
        visible={drawerVisible}
      />
      <NoxTable
        actions={actions}
        columns={columns}
        customTypes={customTypes}
        data={data}
        dragAndDropUrl="content"
        filterContentModel={filterListContentModel}
        filterList={[filterListContentModel, filterListStatus]}
        filterStatus={filterListStatus}
        isDragAndDropEnabled={isDragAndDropEnabled}
        isSearch
        loading={isLoadingContent && isLoadingContentModel}
        onFilterChange={onFilterChange}
        onPageChange={fetchContent}
        selectedFilter={getInitialFilterFromUrl()}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={contentData?.paging.total}
      />
      <Modal
        footerActions={
          <>
            <CButton
              htmlType="submit"
              onClick={(e) => {
                e.preventDefault();
                setOpenDuplicateModal(false);
              }}
              size="large"
              type="secondary"
            >
              Cancel
            </CButton>
            <CButton
              disabled={!isDuplicateSaveEnabled}
              htmlType="submit"
              onClick={(e) => onSaveDuplicate(e)}
              size="large"
              type="primary"
            >
              Create
            </CButton>
          </>
        }
        handleClose={onDuplicateCancel}
        isModalOpen={openDuplicateModal}
        styles={{
          '& .MuiDialogContent-root': {
            paddingBottom: '24px',
            paddingTop: '5px'
          },
          width: '410px'
        }}
        titleContent={<span className="modal-title">Duplication</span>}
      >
        <form
          id="new-content-model-form"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-name"
            id="standard-input-content-model-name"
            label="Name"
            onChange={(e) => handleNameChange(e.target.value)}
            size="large"
            value={contentDraft.name}
          />

          <CStandardInput
            defaultValue="1"
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-name"
            id="standard-input-content-model-name"
            label="Number of copies"
            onChange={(e) => handleCopiesChange(e.target.value)}
            size="large"
            type="number"
            value={totalCopies}
          />
        </form>
      </Modal>
    </div>
  );
};

export function contentDuplicate(body, onSuccess) {
  return async (dispatch, getState) => {
    try {
      const { _id: projectId } = getState().project.currentProject;
      await duplicateContent(projectId, body);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error(`FAILED TO DUPLICATE: ${error}`);
    }
  };
}

export default Content;
